/**
 * The slow-interactions grid (L2): one row per §5 record for the person on
 * show, compact density, grouped headers for the F&O half, the network half and
 * the browser half. Clicking the time opens the record drawer (`?activity=`).
 *
 * The browser half leads with **how the minute was joined** (function v4),
 * because that decides how much the rest of the browser columns are worth: an
 * activity-id match is this interaction's own telemetry, while the 15-minute
 * fallback may hold a neighbouring request the same person made.
 *
 * The network half is where the honesty rule bites: a row whose network layer
 * is not visible renders the explicit chip in the site column and em dashes in
 * the latency columns — never a blank that reads as a clean path. Baselines
 * that do not exist (`form_p50_ms: null`) are em dashes too, not zeros.
 */

import { Badge, DataGrid, type DataGridColumnDef, EmptyState } from "@norican/react";

import type { FoConsoleSlowRow } from "@/lib/api";

import { ClassificationChip, JoinKindChip, NetStateChip } from "./console-chips";
import {
  formatDurationMs,
  formatOrigin,
  hasNetworkBlock,
  JOIN_KIND_DETAIL,
  parseNetState,
  rowOrigin,
  rowRum,
  rumJoinKind,
} from "./console-format";
import { formatBinDateTime, formatCount } from "./status";

/** The exact words a row without browser telemetry shows in the RUM columns. */
const NO_RUM_TEXT = "no browser telemetry";

interface SlowGridProps {
  rows: FoConsoleSlowRow[];
  /** Open the record drawer for this `activity_id`. */
  onOpen: (activityId: string) => void;
  ariaLabel: string;
}

function latencyCell(value: number | null, row: FoConsoleSlowRow) {
  if (!hasNetworkBlock(row)) {
    return (
      <span className="text-fg-subtle tabular-nums" title="network layer not visible for this user">
        —
      </span>
    );
  }
  return <span className="tabular-nums">{formatDurationMs(value)}</span>;
}

export function SlowGrid({ rows, onOpen, ariaLabel }: SlowGridProps) {
  if (rows.length === 0) {
    return (
      <EmptyState
        size="sm"
        title="No slow interaction in this lookback"
        description="No page view crossed 10 s or 3× the form's 28-day median for this person in the window."
      />
    );
  }

  const columns: DataGridColumnDef<FoConsoleSlowRow>[] = [
    {
      id: "interaction",
      header: "Interaction",
      columns: [
        {
          id: "ts",
          header: "Time",
          accessor: (row) => row.ts,
          sortable: true,
          cell: (row) => (
            <button
              type="button"
              className="text-fg rounded text-left font-medium tabular-nums hover:underline focus-visible:outline-2"
              onClick={() => onOpen(row.activity_id)}
              aria-label={`Open the record for ${row.form} at ${formatBinDateTime(row.ts)}`}
            >
              {formatBinDateTime(row.ts)}
            </button>
          ),
        },
        {
          id: "classification",
          header: "Classification",
          accessor: (row) => row.classification,
          sortable: true,
          cell: (row) => <ClassificationChip classification={row.classification} />,
        },
        { id: "form", header: "Form", accessor: (row) => row.form, sortable: true },
        { id: "entity", header: "Entity", accessor: (row) => row.legal_entity, sortable: true },
      ],
    },
    {
      id: "fo",
      header: "F&O",
      columns: [
        {
          id: "duration",
          header: "Total",
          align: "end",
          accessor: (row) => row.duration_ms,
          sortable: true,
          cell: (row) => (
            <span className="font-semibold tabular-nums">{formatDurationMs(row.duration_ms)}</span>
          ),
        },
        {
          id: "form_p50",
          header: "Form p50 / p95",
          align: "end",
          accessor: (row) => row.form_p50_ms,
          sortable: true,
          cell: (row) => (
            <span className="tabular-nums">
              {formatDurationMs(row.form_p50_ms)} / {formatDurationMs(row.form_p95_ms)}
            </span>
          ),
        },
        {
          id: "peers",
          header: "Peers p50 (users)",
          align: "end",
          accessor: (row) => row.peer_p50_ms,
          sortable: true,
          cell: (row) =>
            row.peer_users == null || row.peer_users === 0 ? (
              <span className="text-fg-subtle text-xs">no peers</span>
            ) : (
              <span className="tabular-nums">
                {formatDurationMs(row.peer_p50_ms)} ({formatCount(row.peer_users)})
              </span>
            ),
        },
      ],
    },
    {
      id: "network",
      header: "Network (Cato, same 15 min)",
      columns: [
        {
          id: "site",
          header: "Site · source",
          accessor: (row) => row.site,
          sortable: true,
          cell: (row) => {
            if (hasNetworkBlock(row)) {
              return (
                <span className="flex flex-col">
                  <span className="text-fg text-sm">{row.site}</span>
                  <span className="text-fg-muted text-xs">
                    {[row.src_type, row.pop, row.isp].filter(Boolean).join(" · ")}
                  </span>
                </span>
              );
            }
            // No Cato row: the chip says so, and the Entra country · office
            // says where the person sits — the only location this row has.
            const origin = rowOrigin(row);
            return (
              <span className="flex flex-col gap-0.5">
                <NetStateChip netState="unattributed" />
                {origin && (
                  <span className="text-fg-muted text-xs" title="Entra country · office">
                    {formatOrigin(origin)}
                  </span>
                )}
              </span>
            );
          },
        },
        {
          id: "ttfb",
          header: "TTFB",
          align: "end",
          accessor: (row) => row.ttfb_ms,
          sortable: true,
          cell: (row) => latencyCell(row.ttfb_ms, row),
        },
        {
          id: "tls",
          header: "TLS",
          align: "end",
          accessor: (row) => row.tls_ms,
          sortable: true,
          cell: (row) => latencyCell(row.tls_ms, row),
        },
        {
          id: "tcp",
          header: "TCP",
          align: "end",
          accessor: (row) => row.tcp_ms,
          sortable: true,
          cell: (row) => latencyCell(row.tcp_ms, row),
        },
        {
          id: "net_state",
          header: "Path",
          accessor: (row) => parseNetState(row.net_state),
          sortable: true,
          cell: (row) =>
            parseNetState(row.net_state) === "unattributed" ? (
              <span className="text-fg-subtle text-xs">see site column</span>
            ) : (
              <NetStateChip netState={row.net_state} />
            ),
        },
        {
          id: "exceptions",
          header: "Exc.",
          align: "end",
          accessor: (row) => row.exceptions.length,
          sortable: true,
          cell: (row) =>
            row.exceptions.length === 0 ? (
              <span className="text-fg-subtle tabular-nums">0</span>
            ) : (
              <Badge tone="danger" variant="subtle" size="xs">
                {formatCount(row.exceptions.length)}
              </Badge>
            ),
        },
      ],
    },
    {
      id: "browser",
      header: "Browser (RUM, same minute)",
      columns: [
        {
          id: "rum_server",
          header: "Waiting on F&O",
          align: "end",
          accessor: (row) => rowRum(row)?.server_ms ?? null,
          sortable: true,
          cell: (row) => {
            const rum = rowRum(row);
            if (!rum) {
              return <span className="text-fg-subtle text-xs">{NO_RUM_TEXT}</span>;
            }
            return (
              <span className="tabular-nums" title={`${formatCount(rum.requests)} requests`}>
                {formatDurationMs(rum.server_ms)}
                <span className="text-fg-muted"> of {formatDurationMs(row.duration_ms)}</span>
              </span>
            );
          },
        },
        {
          id: "rum_join",
          header: "Joined by",
          accessor: (row) => (rowRum(row) ? rumJoinKind(rowRum(row)) : null),
          sortable: true,
          cell: (row) => {
            const rum = rowRum(row);
            if (!rum) {
              return <span className="text-fg-subtle text-xs">{NO_RUM_TEXT}</span>;
            }
            // Which browser minute this is decides what the whole browser half
            // of the row is worth, so it is a column rather than a tooltip: an
            // activity-id match is this interaction's own telemetry, the
            // 15-minute fallback may hold a neighbouring request.
            const joinKind = rumJoinKind(rum);
            return (
              <span title={JOIN_KIND_DETAIL[joinKind]}>
                <JoinKindChip joinKind={joinKind} />
              </span>
            );
          },
        },
        {
          id: "rum_inp",
          header: "Interaction p50",
          align: "end",
          accessor: (row) => rowRum(row)?.interaction_p50_ms ?? null,
          sortable: true,
          cell: (row) => {
            const rum = rowRum(row);
            if (!rum) {
              return (
                <span className="text-fg-subtle tabular-nums" title={NO_RUM_TEXT}>
                  —
                </span>
              );
            }
            if (rum.interaction_p50_ms == null) {
              return (
                <span
                  className="text-fg-subtle tabular-nums"
                  title="Event Timing arrives with extension 0.2; this minute carries none — unknown, not zero"
                >
                  —
                </span>
              );
            }
            return (
              <span className="tabular-nums">
                {formatDurationMs(rum.interaction_p50_ms)}
                {rum.interaction_max_ms != null && (
                  <span className="text-fg-muted">
                    {" "}
                    / {formatDurationMs(rum.interaction_max_ms)}
                  </span>
                )}
              </span>
            );
          },
        },
        {
          id: "rum_ttfb",
          header: "TTFB p50 · RTT",
          align: "end",
          accessor: (row) => rowRum(row)?.ttfb_p50_ms ?? null,
          sortable: true,
          cell: (row) => {
            const rum = rowRum(row);
            if (!rum) {
              return (
                <span className="text-fg-subtle tabular-nums" title={NO_RUM_TEXT}>
                  —
                </span>
              );
            }
            return (
              <span className="tabular-nums" title={rum.conn_type || undefined}>
                {formatDurationMs(rum.ttfb_p50_ms)} · {formatDurationMs(rum.rtt_ms)}
              </span>
            );
          },
        },
      ],
    },
  ];

  return (
    <div className="h-[28rem]">
      {/* DataGrid needs a definite height for its sticky header/scroller. */}
      <DataGrid<FoConsoleSlowRow>
        data={rows}
        getRowId={(row) => row.activity_id}
        density="compact"
        aria-label={ariaLabel}
        columns={columns}
      />
    </div>
  );
}
